"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import { Tooltip } from "@heroui/tooltip"; // Import Tooltip
import axios from "axios";

export const StockPeersCards = ({ company = {}, ticker }) => {
  const { industry = "", frc_covered = false } = company;

  const [realTimePrice, setRealTimePrice] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [country, setCountry] = useState("");
  const [sector, setSector] = useState("");
  const [volAvg, setVolAvg] = useState("");
  const [mktCap, setmktCap] = useState("");
  const [peRatioTTM, setPeRatioTTM] = useState("");
  const defaultImageUrl = "/images/NoFoundImage.webp"; // URL of the default image
  const [exchange, setExchange] = useState("");
  const [changeInValue, setChangeInValue] = useState("");
  const [percentageChange, setPercentageChange] = useState("");
  const [currency, setCurrency] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");

  // Utility function to format numbers
  const formatNumber = (num) => {
    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
    return num.toString();
  };

  // Fetch real-time price without caching as the value is highly dynamic
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await axios.post(
          "/api/company/stock-peers/quote",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = response.data[0];
        setRealTimePrice(Number(data.price).toFixed(2));
        setChangeInValue(data.change);
        setPercentageChange(data.changesPercentage);
      } catch (error) {
        console.error(
          "Failed to fetch price:",
          error.response ? error.response.data : error.message
        );
        setRealTimePrice("N/A");
        setChangeInValue("N/A");
        setPercentageChange("N/A");
      }
    };
    fetchPrice();
  }, [ticker]);

  // Modify this useEffect to also fetch country, sector, and volAvg
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      setIsLoading(true); // Set loading to true when starting to fetch

      try {
        const response = await axios.post(
          "/api/company/details",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.status === 200) {
          throw new Error("Network response was not ok");
        }
        const data = await response.data;

        const profile = data[0];
        setCompanyLogo(profile.image);
        setCountry(profile.country);
        setVolAvg(formatNumber(profile.volAvg));
        setmktCap(formatNumber(profile.mktCap));
        setCurrency(profile.currency);
        setExchange(profile.exchangeShortName);
        setSector(profile.sector);
        setCompanyName(profile.companyName); // Correctly using setCompanyName here
      } catch (error) {
        console.error(
          "There has been a problem with your fetch operation:",
          error
        );
      }
      setIsLoading(false); // Set loading to false after fetch completes or fails
    };

    fetchCompanyDetails();
  }, [ticker]);

  // Define a function to get the currency symbol
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case "USD":
        return "US $";
      case "CAD":
        return "CA $";
      default:
        return "";
    }
  };

  useEffect(() => {
    const fetchRatios = async () => {
      try {
        const response = await axios.post(
          "/api/company/ratios",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status !== 200) {
          throw new Error("Network response was not ok");
        }

        // Check if the response data has the expected structure
        if (
          response.data &&
          response.data.length > 0 &&
          response.data[0].priceEarningsRatioTTM !== undefined
        ) {
          setPeRatioTTM(
            Number(response.data[0].priceEarningsRatioTTM).toFixed(2)
          );
        } else {
          // Handle unavailable data
          setPeRatioTTM("N/A");
        }
      } catch (error) {
        console.error(
          "There has been a problem with your fetch operation:",
          error
        );
        setPeRatioTTM("N/A");
      }
    };

    fetchRatios();
  }, [ticker]);

  // Function to handle image load error
  const handleImageError = () => {
    setCompanyLogo(defaultImageUrl); // Set to default image URL on error
  };

  // Function to truncate company name
  const truncateName = (name, maxLength = 15) => {
    if (name.length > maxLength) {
      return `${name.substring(0, maxLength)}...`; // Truncate and append '...'
    }
    return name;
  };

  return (
    <div
      className="relative bg-E6E6E6 shadow-sm rounded-xl p-2 justify-between h-350 "
      style={{ width: "250px" }}
    >
      {" "}
      {/* Adjust the width here */}
      {/* Header with Logo, Ticker, Company Name, and Price */}
      <div className="flex mt-2 justify-between items-start">
        <div className="flex items-center">
          <div className="logo-container p-2">
            <Image
              src={companyLogo || defaultImageUrl}
              alt={`${name} Logo`}
              unoptimized
              width={40} // You may need to adjust this
              height={40} // You may need to adjust this
              onError={handleImageError} // Handle image load error
            />
          </div>
          <div>
            <p className="font-bold text-md">{ticker} </p>

            {/* Wrap the company name with Tooltip */}
            <Tooltip content={companyName} placement="bottom">
              <p className="text-xs text-black">{truncateName(companyName)}</p>
            </Tooltip>
          </div>
        </div>
        <div className="text-right pr-5 pt-1 ">
          <p className="text-sm  text-gray-800"> Price</p>

          <p className="font-bold text-sm">
            {realTimePrice
              ? ` ${getCurrencySymbol(currency)}${Number(realTimePrice).toFixed(
                  2
                )}`
              : "N/A"}
          </p>
          {changeInValue && percentageChange && Number(realTimePrice) !== 0 ? (
            <p
              className="text-xs font-semibold"
              style={{
                color: Number(changeInValue) >= 0 ? "green-600" : "red-600", // Conditional styling based on change in value
              }}
            >
              {`${Number(changeInValue).toFixed(2)} (${Number(
                percentageChange
              ).toFixed(2)}%)`}
            </p>
          ) : (
            <p className="text-xs">N/A</p>
          )}
        </div>
      </div>
      {/* Content Sections */}
      <div className="flex-grow p-2 ">
        <p className="text-sm mt-7">
          <span className="font-semibold">Country:</span>{" "}
          {country ? country : "N/A"}
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">Exchange:</span>{" "}
          {exchange ? exchange : "N/A"}
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">Sector:</span>{" "}
          <Tooltip content={sector} placement="bottom">
            <span>{sector ? truncateName(sector) : "N/A"}</span>
          </Tooltip>
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">Industry:</span>{" "}
          <Tooltip content={industry} placement="bottom">
            <span>{truncateName(industry)}</span>
          </Tooltip>
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">Market Cap:</span> {mktCap}
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">Volume Avg:</span> {volAvg}
        </p>
        <p className="text-sm mt-1">
          <span className="font-semibold">P/E:</span> {peRatioTTM || "N/A"}
        </p>
        {/* Display 'Covered by FRC' label only if frc_covered is true */}
        {frc_covered && (
          <p className=" text-1B51CA text-sm flex-grow  mt-1">Covered by FRC</p>
        )}
      </div>
    </div>
  );
};
